import { usePersonalViewsOptimistic } from '../../../services/globalFilters/globalFiltersOptimistic';

/**
 * Personal views mutation wrapper with optimistic updates
 *
 * **Why this wrapper?**
 *
 * This component was created to solve a specific problem: mutation URLs are dynamic
 * and come directly from list items (e.g., `option.setDefaultAttributeUrl`).
 *
 * **Problem encountered:**
 * - React Query hooks require a fixed URL at creation time
 * - In our case, each item has its own URL (favorite, delete, etc.)
 * - We cannot create a hook per item (this would create too many hooks)
 *
 * **Solution considered:**
 * **Wrapper with render prop** (current solution)
 *    - The hook is created with the dynamic URL passed as a prop
 *    - The component encapsulates the mutation logic
 *    - Provides `mutate` and `isPending` via render prop
 *
 * **Usage:**
 * ```jsx
 * <PersonalViewsMutationWrapper
 *   mutationKey={GLOBAL_FILTERS_QUERY_KEY}
 *   url={option.setDefaultAttributeUrl}
 *   path={page?.path}
 *   requestBody={{ value: 'oui' }}
 *   optimisticPersonalViewsPatch={{ defaultOption: '3' }}
 * >
 *   {({ mutate, isPending }) => (
 *     <IconButton
 *       onClick={mutate}
 *       disabled={isPending}
 *     />
 *   )}
 * </PersonalViewsMutationWrapper>
 * ```
 */

/**
 * @typedef {object} PersonalViewsMutationVariables
 * @property {Record<string, unknown>} [requestBody] - Payload sent to the API
 * @property {Partial<ApiManagerPersonalViews>} [optimisticPersonalViewsPatch] - Cache patch for personalViews
 */

/**
 * @param {object} props
 * @param {string} props.mutationKey - The mutation key to use for the optimistic update
 * @param {string} props.url - The URL to call (dynamic, comes from the item)
 * @param {string} props.path - The page path for the query key
 * @param {Record<string, unknown>} [props.requestBody = {}] - Payload sent to the API
 * @param {Partial<ApiManagerPersonalViews>} [props.optimisticPersonalViewsPatch = {}] - Cache patch for personalViews
 * @param {(props: {mutate: () => void, isPending: boolean}) => React.ReactNode} props.children - Render prop function
 * @returns {React.ReactNode}
 */
export function PersonalViewsMutationWrapper({
  mutationKey,
  url,
  path,
  requestBody = {},
  optimisticPersonalViewsPatch = {},
  children,
}) {
  const { mutate, isPending } = usePersonalViewsOptimistic(mutationKey, url, path);

  return children({
    mutate: () => mutate({
      requestBody,
      optimisticPersonalViewsPatch,
    }),
    isPending,
  });
}
